import React, { useState, useEffect } from 'react';
import { Stethoscope, CheckCircle2, RefreshCw, Plus, Pencil, Archive, X } from 'lucide-react';
import toast from 'react-hot-toast';
import { opendentalAPI } from '../services/api';

// Settings UI for the per-org OpenDental integration. One org can run
// several branches, each with its own OpenDental database + customer key.
// The developer key is server-side only (env var); admins only manage each
// branch's customer key + provider defaults. Patient/appointment sync fires
// when a lead transitions to status='won' and a branch is chosen in the
// booking modal.
const EMPTY_DRAFT = {
  name: '', customerKey: '', defaultProviderId: '', defaultOperatoryId: '', defaultAppointmentTypeNum: '',
};

const STATUS_BADGE = {
  connected:      { cls: 'bg-green-100 text-green-700', label: 'Connected' },
  configured:     { cls: 'bg-amber-100 text-amber-700', label: 'Configured — test to verify' },
  not_configured: { cls: 'bg-gray-100 text-gray-600',   label: 'Not configured' },
};

const OpenDentalSettings = () => {
  const [data, setData] = useState(null);          // { enabled, devKeyConfigured, locations, ... }
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);    // null | 'new' | locId
  const [draft, setDraft] = useState(EMPTY_DRAFT);
  const [saving, setSaving] = useState(false);
  const [testingId, setTestingId] = useState(null);

  useEffect(() => { load(); }, []);

  const load = async () => {
    try {
      const { data } = await opendentalAPI.getSettings();
      setData(data);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to load OpenDental settings');
    } finally {
      setLoading(false);
    }
  };

  const onChange = (e) => {
    const { name, value } = e.target;
    setDraft((d) => ({ ...d, [name]: value }));
  };

  const startAdd = () => { setDraft(EMPTY_DRAFT); setEditing('new'); };
  const startEdit = (loc) => {
    setDraft({
      name: loc.name || '',
      customerKey: '', // never prefill the secret
      defaultProviderId: loc.defaultProviderId ?? '',
      defaultOperatoryId: loc.defaultOperatoryId ?? '',
      defaultAppointmentTypeNum: loc.defaultAppointmentTypeNum ?? '',
    });
    setEditing(loc.id);
  };
  const cancelEdit = () => { setEditing(null); setDraft(EMPTY_DRAFT); };

  const onSave = async (e) => {
    e.preventDefault();
    if (!draft.name.trim()) { toast.error('Branch name is required'); return; }
    setSaving(true);
    try {
      // Only send the key when the operator typed one (blank keeps existing).
      const payload = {
        name: draft.name.trim(),
        defaultProviderId: draft.defaultProviderId,
        defaultOperatoryId: draft.defaultOperatoryId,
        defaultAppointmentTypeNum: draft.defaultAppointmentTypeNum,
      };
      if (draft.customerKey.trim()) payload.customerKey = draft.customerKey.trim();

      let saved;
      if (editing === 'new') {
        if (!draft.customerKey.trim()) payload.customerKey = ''; // explicit empty for new
        ({ data: saved } = await opendentalAPI.addLocation(payload));
      } else {
        ({ data: saved } = await opendentalAPI.updateLocation(editing, payload));
      }
      cancelEdit();
      await load();

      // Auto-test after save when a key is configured, for instant feedback.
      if (saved?.isConfigured) {
        try {
          const { data: tc } = await opendentalAPI.testLocation(saved.id);
          if (tc?.ok) toast.success('Saved and connection verified ✅');
          else toast.error(`Saved but connection failed: ${tc?.error || 'unknown error'}`);
        } catch (tcErr) {
          toast.error(`Saved but connection test failed: ${tcErr.response?.data?.error || tcErr.message}`);
        }
        await load();
      } else {
        toast.success('Branch saved (add a customer key to enable sync).');
      }
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to save branch');
    } finally {
      setSaving(false);
    }
  };

  const onTest = async (loc) => {
    setTestingId(loc.id);
    try {
      const { data: tc } = await opendentalAPI.testLocation(loc.id);
      if (tc?.ok) toast.success(`${loc.name}: connection OK — practice database reached.`);
      else toast.error(tc?.error || 'Connection failed');
      await load();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Connection failed');
      await load();
    } finally {
      setTestingId(null);
    }
  };

  const onArchive = async (loc) => {
    if (!window.confirm(`Archive branch "${loc.name}"? It will stop syncing. Historical leads keep their records.`)) return;
    try {
      await opendentalAPI.archiveLocation(loc.id);
      toast.success(`Branch "${loc.name}" archived`);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to archive branch');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading OpenDental settings…</p>
        </div>
      </div>
    );
  }

  const fmtTs = (d) => d ? new Date(d).toLocaleString() : '—';
  const locations = data?.locations || [];

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-white shadow-lg rounded-lg">
          <div className="px-6 py-4 border-b border-gray-200 flex items-start justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                <Stethoscope size={24} className="text-teal-600" />
                OpenDental Settings
              </h1>
              <p className="mt-1 text-sm text-gray-600">
                Push leads to your OpenDental practice as patients and book appointments when a lead becomes <strong>Won</strong>. Add one branch per practice database.
              </p>
            </div>
            <button
              onClick={startAdd}
              className="flex items-center gap-2 px-4 py-2 bg-teal-600 text-white rounded-md hover:bg-teal-700"
            >
              <Plus size={16} /> Add Branch
            </button>
          </div>

          {!data?.devKeyConfigured && (
            <div className="mx-6 mt-4 p-4 rounded-md bg-amber-50 border border-amber-300 text-amber-900 text-sm">
              <strong>Server warning:</strong> OPENDENTAL_DEV_KEY is not set on the server. Patient sync will fail until the system administrator adds it.
            </div>
          )}

          {data?.pendingSyncCount > 0 && (
            <div className="mx-6 mt-4 p-3 rounded-md bg-amber-50 border border-amber-300 text-amber-900 text-sm">
              {data.pendingSyncCount} {data.pendingSyncCount === 1 ? 'lead is' : 'leads are'} waiting to sync to OpenDental
            </div>
          )}

          {/* Add/Edit form */}
          {editing && (
            <form onSubmit={onSave} className="mx-6 mt-5 p-5 border-2 border-teal-200 rounded-lg bg-teal-50/40">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">
                  {editing === 'new' ? 'New Branch' : 'Edit Branch'}
                </h3>
                <button type="button" onClick={cancelEdit} className="text-gray-400 hover:text-gray-600">
                  <X size={18} />
                </button>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Branch Name</label>
                  <input
                    type="text" name="name" value={draft.name} onChange={onChange}
                    placeholder="e.g. Downtown Clinic"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Customer Key</label>
                  <input
                    type="password" name="customerKey" value={draft.customerKey} onChange={onChange}
                    placeholder={editing !== 'new' ? 'Leave blank to keep current key' : 'Paste this branch’s OpenDental customer key'}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500 font-mono"
                  />
                  <p className="text-xs text-gray-500 mt-1">Generate in the OpenDental portal (Customer Keys → Create Key) and paste the same key into this branch’s OpenDental database via Setup → API.</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Default Provider ID <span className="text-xs text-gray-400">(optional)</span></label>
                  <input type="number" name="defaultProviderId" value={draft.defaultProviderId} onChange={onChange} placeholder="e.g. 1"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Default Operatory ID <span className="text-xs text-gray-400">(optional)</span></label>
                  <input type="number" name="defaultOperatoryId" value={draft.defaultOperatoryId} onChange={onChange} placeholder="e.g. 1"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Default Appointment Type # <span className="text-xs text-gray-400">(optional)</span></label>
                  <input type="number" name="defaultAppointmentTypeNum" value={draft.defaultAppointmentTypeNum} onChange={onChange} placeholder="e.g. 1"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500" />
                </div>
              </div>
              <div className="mt-5 flex gap-3">
                <button type="submit" disabled={saving} className="px-6 py-2 bg-teal-600 text-white rounded-md hover:bg-teal-700 disabled:opacity-50">
                  {saving ? 'Saving…' : 'Save Branch'}
                </button>
                <button type="button" onClick={cancelEdit} className="px-6 py-2 bg-white border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50">
                  Cancel
                </button>
              </div>
            </form>
          )}

          {/* Branch cards */}
          <div className="px-6 py-6 space-y-4">
            {locations.length === 0 && !editing && (
              <div className="text-center py-10 text-gray-500">
                <Stethoscope size={32} className="mx-auto text-gray-300 mb-2" />
                <p>No branches yet. Click <strong>Add Branch</strong> to connect your first OpenDental practice.</p>
              </div>
            )}

            {locations.map((loc) => {
              const badge = STATUS_BADGE[loc.status] || STATUS_BADGE.not_configured;
              return (
                <div key={loc.id} className="border border-gray-200 rounded-lg p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="text-lg font-semibold text-gray-900 truncate">{loc.name || 'Unnamed branch'}</h3>
                        <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${badge.cls}`}>{badge.label}</span>
                      </div>
                      <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1 text-xs text-gray-600">
                        <div><span className="text-gray-400">Key:</span> {loc.customerKey ? <span className="font-mono">{loc.customerKey}</span> : <span className="text-gray-400">none</span>}</div>
                        <div><span className="text-gray-400">Default provider:</span> {loc.defaultProviderId ?? '—'}</div>
                        <div><span className="text-gray-400">Last sync:</span> {fmtTs(loc.lastSyncedAt)}</div>
                        <div className={loc.lastErrorMessage ? 'text-red-600' : ''}>
                          <span className="text-gray-400">Last error:</span> {loc.lastErrorMessage ? `${loc.lastErrorMessage} (${fmtTs(loc.lastErrorAt)})` : 'None'}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <button onClick={() => onTest(loc)} disabled={!loc.isConfigured || testingId === loc.id}
                        className="flex items-center gap-1 px-3 py-1.5 text-sm bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50">
                        <RefreshCw size={14} className={testingId === loc.id ? 'animate-spin' : ''} />
                        {testingId === loc.id ? 'Testing…' : 'Test'}
                      </button>
                      <button onClick={() => startEdit(loc)} title="Edit"
                        className="p-2 text-gray-500 hover:text-teal-600 hover:bg-teal-50 rounded-md">
                        <Pencil size={16} />
                      </button>
                      <button onClick={() => onArchive(loc)} title="Archive"
                        className="p-2 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-md">
                        <Archive size={16} />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="px-6 py-4 bg-gray-50 border-t border-gray-200">
            <h3 className="text-lg font-medium text-gray-900 mb-3 flex items-center gap-2">
              <CheckCircle2 size={18} className="text-teal-600" /> How it works
            </h3>
            <ul className="text-sm text-gray-600 list-disc list-inside space-y-1">
              <li>Each branch maps to one OpenDental practice database (its own customer key).</li>
              <li>When a lead becomes <strong>Won</strong>, the booking modal asks which branch, then provider and time. The patient + appointment are created in that branch.</li>
              <li>Idempotent — once a lead has a PatNum stamped, subsequent saves don’t duplicate.</li>
              <li>If OpenDental is offline at booking time, the lead is saved as pending and reconciled automatically once it’s back.</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OpenDentalSettings;
